"use client";

/**
 * CreatenanoMark — small animated brand mark for inline use (button icons,
 * menu badges, etc.). Renders the "cn" letterform inside a rotating
 * gradient ring with a soft pulse. Self-contained SVG + motion/react —
 * no WebGL, no external assets, scales cleanly to any size.
 */
import { motion } from "motion/react";

interface Props {
  /** Pixel size of the square mark. Defaults to 20. */
  size?: number;
  /** Stroke / text color. Defaults to currentColor. */
  color?: string;
  className?: string;
}

export default function CreatenanoMark({
  size = 20,
  color = "currentColor",
  className,
}: Props) {
  const id = `cn-mark-grad`;
  return (
    <span
      className={className}
      style={{
        position: "relative",
        display: "inline-flex",
        width: size,
        height: size,
      }}
    >
      {/* Soft pulse halo behind the ring */}
      <motion.span
        aria-hidden
        animate={{
          opacity: [0.35, 0.6, 0.35],
          scale: [0.95, 1.05, 0.95],
        }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${color}33 0%, transparent 70%)`,
        }}
      />

      {/* Rotating gradient ring */}
      <motion.svg
        viewBox="0 0 32 32"
        width={size}
        height={size}
        style={{ position: "absolute", inset: 0 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
      >
        <defs>
          <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="1" />
            <stop offset="55%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <circle
          cx="16"
          cy="16"
          r="14"
          fill="none"
          stroke={`url(#${id})`}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </motion.svg>

      {/* Static "cn" letterform */}
      <svg
        viewBox="0 0 32 32"
        width={size}
        height={size}
        style={{ position: "absolute", inset: 0 }}
        aria-hidden
      >
        <text
          x="16"
          y="21"
          textAnchor="middle"
          fontFamily="'Nexa', 'Nunito', system-ui, sans-serif"
          fontSize="13"
          fontWeight={900}
          fill={color}
          letterSpacing="-0.04em"
        >
          cn
        </text>
      </svg>
    </span>
  );
}
